<script setup>
import {useReservationsStore, useRestaurantsStore, useUserGroupsStore, useUsersStore} from '@/stores'
import { useDesignSystem } from '@/composables/useDesignSystem'
import moment from 'moment'
import Tooltip from '@/layouts/components/Tooltip.vue'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import {
  emailValidator,
  requiredValidator,
} from '@validators'

const emit = defineEmits([
  'update:modelValue',
  'submit',
  'update:isDialogVisible'
])

const { dialogWidth } = useDesignSystem()
const dWidth = dialogWidth('md')

const userGroupsStore = useUserGroupsStore()
const usersStore = useUsersStore()
let adminEditingUserGroupSubscription = null
let myUser = null;
({ myUser } = storeToRefs(usersStore));
({  adminEditingUserGroupSubscription } = storeToRefs(userGroupsStore))

const isDialogVisible = ref(false)
const isConfirmCancelDialogOpen = ref(false)

const form = ref()
const valid = ref(true)

const makingNewSubscription = ref(false)
const idOfEditingSubscription = ref(null)

//calcualted value indexOfEditingSubscription from idOfEditingSubscription
const indexOfEditingSubscription = computed(() => {
  if(idOfEditingSubscription.value){
    for(let i = 0; i < adminEditingUserGroupSubscription.value.subscriptions.length; i++){
      if(adminEditingUserGroupSubscription.value.subscriptions[i].id == idOfEditingSubscription.value){
        console.log("found it", i)
        
        return i
      }
    }
  }
  
  return null
})


const addNewSubscription = async () => {
  console.log("Adding New")
  adminEditingUserGroupSubscription.value.subscriptions.push(  {
    planId: "enterprise-1cycle-annual",
    status: "New",
    price: 0,
    numberOfBillingCycles: 1,
    numberOfSeats: 10,
    firstBillingDate: moment().format('YYYY-MM-DD'),
    poNumber: "",
    groupOwnerMessage: "",
    paymentTerms: "None"
  })
  makingNewSubscription.value = true
}


const subscriptionToCancel = ref()
const cancelPlatinumSubscription = async () => {
  //find subscription that matches id from subscriptionToCancel and set canceled
  for(let subscription of adminEditingUserGroupSubscription.value.subscriptions){
    if(subscription.id == subscriptionToCancel.value){
      subscription.status = "Canceled"
    }
  }

  userGroupsStore.saveAdminEditingUserGroupSubscription()
}

const onFormSubmit = async () => {
  form.value.validate().then(({valid: isValid}) => {
    if(isValid){
      userGroupsStore.saveAdminEditingUserGroupSubscription()

    }

  })
}

//check to see if we should pop up any dialogs when the store changes
//this seems hacky
userGroupsStore.$subscribe((mutation, state) => {
  //if wasn't visible before and now is, reset some things
  if(!isDialogVisible.value && (state.adminEditingUserGroupSubscription != null)) makingNewSubscription.value = false

  isDialogVisible.value = ( state.adminEditingUserGroupSubscription != null)
})
</script>

<template>
  <ConfirmDialog
    v-model:is-dialog-visible="isConfirmCancelDialogOpen"
    confirmation-msg="Cancel this Premium Membership?"
    confirm-btn-text="Yes"
    cancel-btn-text="No"
    @confirm="cancelPlatinumSubscription"
  />
  <VDialog
    v-if="adminEditingUserGroupSubscription && myUser.userLevel == 'admin'"
    v-model="isDialogVisible"
    :width="dWidth"
    persistent
    no-click-animation
    :retain-focus="false"
    scroll-strategy="none"
  >
    <VCard class="pa-sm-9 pa-1">
      <VCardItem class="text-center">
        <VCardTitle class="text-h5">
          {{ adminEditingUserGroupSubscription?.name }} - Platinum Membership
        </VCardTitle>
      </VCardItem>

      <VCardText>
        <!-- 👉 Form -->
        <VForm
          ref="form"
          class="mt-6"
          @submit.prevent="onFormSubmit"
        >
          <VRow>
            <VCol
              cols="12"
            >
              <template
                v-for="(subscription, key) in adminEditingUserGroupSubscription.platinumSubscriptionsByState"
                :key="key"
              >
                <VCard
                  v-if="subscription"
                  class="ma-2 pa-2"
                >
                  <h2 class="account-card-name">
                    {{ subscription.status }}
                  </h2>
                  <div>
                    <b>
                      <u>{{ subscription.name }}</u>
                    </b>
                  </div>
                  <div v-if="subscription?.discountGroupMembershipMessage"> 
                    <b>Membership Message: </b>{{ subscription.discountGroupMembershipMessage }}
                  </div>
                  <div v-if="subscription?.sourceGroupMembershipMessage"> 
                    <b>Membership Message: </b>{{ subscription.sourceGroupMembershipMessage }}
                  </div>
                  <div v-if="subscription?.sourceUserName"> 
                    <b>Primary Member: </b>{{ subscription.sourceUserName }} ({{ subscription.sourceUserEmail }})
                  </div>
                  <div v-if="subscription?.membershipMessage"> 
                    <b>Membership Message: </b>{{ subscription.membershipMessage }}
                  </div>
                  <div>
                    <b>Seats:</b> {{ subscription?.numberOfSeats }}
                  </div>
                  <div>
                    <b>Cost:</b> ${{ (subscription?.price).toFixed(2) }}
                  </div>
                  <div v-if="subscription?.balance">
                    Balance owed: ${{ (subscription.balance).toFixed(2) }}
                  </div>
                  <div v-if="subscription?.numberOfBillingCycles && subscription?.numberOfBillingCycles == 1 && subscription.paidThroughDate">
                    <b>Term:</b> {{ moment(subscription.firstBillingDate).format("M/D/YY") }} - {{ moment(subscription.paidThroughDate).format("M/D/YY") }}
                  </div>
                  <div v-if="subscription?.numberOfBillingCycles && subscription?.numberOfBillingCycles == 1 && subscription.nextBillingDate">
                    <b>Term Starts:</b> {{ subscription.nextBillingDate ? moment(subscription.nextBillingDate).format("M/D/YY") : "N/A" }}
                  </div>
                  <VBtn
                    v-if="key != 'lastCanceled'"
                    density="compact"
                    @click="subscriptionToCancel = subscription.id; isConfirmCancelDialogOpen = true"
                  >
                    Cancel
                  </VBtn>
                  <VBtn
                    v-if="subscription.status == 'Active'"
                    density="compact"
                    class="ml-2"
                    @click="idOfEditingSubscription = subscription.id"
                  >
                    Edit
                  </VBtn>
                </VCard>
              </template>
              <div class="pa-0">
                <VCard
                  v-if="makingNewSubscription"
                  title="New Platinum Membership"
                  class="mt-5"
                >
                  <VSelect
                    v-model="adminEditingUserGroupSubscription.subscriptions[adminEditingUserGroupSubscription.subscriptions.length-1].planId"
                    class="pa-4"
                    label="Plan"
                    :items="[
                      {text: 'Platinum - 1 Month', value: 'enterprise-1cycle-1month'}, 
                      {text: 'Platinum - 3 Month', value: 'enterprise-1cycle-3month'}, 
                      {text: 'Platinum - Annual', value: 'enterprise-1cycle-annual'}
                    ]"
                    item-title="text"
                    item-value="value"
                  />
                  <div class="pa-4">
                    <AppDateTimePicker
                      v-model="adminEditingUserGroupSubscription.subscriptions[adminEditingUserGroupSubscription.subscriptions.length-1].firstBillingDate"
                      class="pa-4"
                      label="Start Date"
                      :config="{ disableMobile: true, altInput: true, altFormat: 'l, n/j', dateFormat: 'Y-m-d', defaultDate: moment().format('YYYY-MM-DD')}"
                      :rules="[requiredValidator]"
                      required
                    />
                  </div>
                  <VTextField
                    v-model="adminEditingUserGroupSubscription.subscriptions[adminEditingUserGroupSubscription.subscriptions.length-1].price"
                    class="pa-4"
                    label="Total Price"
                    type="number"
                    hint="Total amount that will be paid for this membership."
                  />
                  <VTextField
                    v-model="adminEditingUserGroupSubscription.subscriptions[adminEditingUserGroupSubscription.subscriptions.length-1].numberOfSeats"
                    class="pa-4"
                    label="Seats"
                    type="number"
                    hint="Number of seats being paid for (non-enforced and can be est.)"
                  />
                  <VTextField
                    v-model="adminEditingUserGroupSubscription.subscriptions[adminEditingUserGroupSubscription.subscriptions.length-1].poNumber"
                    class="pa-4"
                    label="PO Number (optional)"
                  />
                  <VTextField
                    v-model="adminEditingUserGroupSubscription.subscriptions[adminEditingUserGroupSubscription.subscriptions.length-1].groupOwnerMessage"
                    class="pa-4"
                    label="Account Owner Message"
                  />
                  <VCardActions>
                    <VBtn
                      @click="userGroupsStore.saveAdminEditingUserGroupSubscription(); makingNewSubscription=false;"
                    >
                      Save
                    </VBtn>
                  </VCardActions>
                </VCard>
                <VCard
                  v-else-if="indexOfEditingSubscription == 0 || indexOfEditingSubscription"
                  :title="`Edit ${adminEditingUserGroupSubscription.subscriptions[indexOfEditingSubscription].name} Membership`"
                  class="mt-5"
                >
                  <div class="pa-4">
                    <AppDateTimePicker
                      v-model="adminEditingUserGroupSubscription.subscriptions[indexOfEditingSubscription].firstBillingDate"
                      class="pa-4"
                      label="Start Date"
                      :config="{ disableMobile: true, altInput: true, altFormat: 'l, n/j', dateFormat: 'Y-m-d', defaultDate: moment().format('YYYY-MM-DD')}"
                      :rules="[requiredValidator]"
                      required
                    />
                  </div>
                  <div class="pa-4">
                    <AppDateTimePicker
                      v-model="adminEditingUserGroupSubscription.subscriptions[indexOfEditingSubscription].paidThroughDate"
                      class="pa-4"
                      label="Last Day"
                      :config="{ disableMobile: true, altInput: true, altFormat: 'l, n/j', dateFormat: 'Y-m-d', defaultDate: moment().format('YYYY-MM-DD')}"
                      :rules="[requiredValidator]"
                      required
                    />
                  </div>
                  <VTextField
                    v-model.number="adminEditingUserGroupSubscription.subscriptions[indexOfEditingSubscription].price"
                    class="pa-4"
                    label="Total Price"
                    type="number"
                    hint="Total amount that will be paid for this membership."
                  />
                  <VTextField
                    v-model="adminEditingUserGroupSubscription.subscriptions[indexOfEditingSubscription].numberOfSeats"
                    class="pa-4"
                    label="Seats"
                    type="number"
                    hint="Number of seats being paid for (non-enforced and can be est.)"
                  />
                  <VTextField
                    v-model="adminEditingUserGroupSubscription.subscriptions[indexOfEditingSubscription].poNumber"
                    class="pa-4"
                    label="PO Number (optional)"
                  />
                  <VTextField
                    v-model="adminEditingUserGroupSubscription.subscriptions[indexOfEditingSubscription].groupOwnerMessage"
                    class="pa-4"
                    label="Account Owner Message"
                  />
                  <VCardActions>
                    <VBtn
                      @click="userGroupsStore.saveAdminEditingUserGroupSubscription(); idOfEditingSubscription=null;"
                    >
                      Save
                    </VBtn>
                  </VCardActions>
                </VCard>
                <VBtn
                  v-else-if="!adminEditingUserGroupSubscription.platinumSubscriptionsByState?.pending"
                  class="mt-4"
                  @click="addNewSubscription"
                >
                  New Membership
                </VBtn>
              </div>
            </VCol>
            <VCol
              cols="12"
            />


            <!-- 👉 Submit and Cancel -->
            <VCol
              cols="12"
              class="d-flex flex-wrap justify-center gap-4"
            >
              <VBtn
                color="secondary"
                variant="tonal"
                @click="userGroupsStore.cancelAdminEditUserGroupSubscription()"
              >
                Close
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </VDialog>
</template>
